import Dexie, { Table } from 'dexie';
import db, { DataRepository, Database, Key } from '@/db';
import { PlayerForSaveDto, Contract } from '../../api/models';
import { searchPlayersByName } from '@/library/PlayerSearchHelper';

export type TABLE_TYPE = Table<PlayerForSaveDto>;

export const TABLE = 'player';
const SCHEMA = 'playerId, fullName, defaultTourName, createDate';

class PlayerDatabase implements DataRepository<PlayerForSaveDto> {
  constructor(private db: Database) {
    db.registerModelSchema(TABLE, SCHEMA);
  }

  get(key: Key): Promise<PlayerForSaveDto | undefined> {
    return this.db[TABLE].get(key);
  }

  delete(key: Key): Promise<void> {
    return this.db[TABLE].delete(key);
  }

  update(payload: PlayerForSaveDto, key: Key): Promise<PlayerForSaveDto> {
    return this.db[TABLE].put(payload, key);
  }

  bulkAdd(payload: PlayerForSaveDto[]): Promise<PlayerForSaveDto[]> {
    return this.db[TABLE].bulkPut(payload);
  }

  bulkDelete(keys: Key[]): Promise<void> {
    return this.db[TABLE].bulkDelete(keys);
  }

  async applyDelta(players: PlayerForSaveDto[]): Promise<void> {
    if (!players.length) return;

    const toUpsert: PlayerForSaveDto[] = [];
    const toDeleteIds: number[] = [];

    // Split active vs inactive in one pass
    for (const player of players) {
      if (player.isActive) {
        toUpsert.push(player);
      } else {
        toDeleteIds.push(player.playerId);
      }
    }

    // Apply delta in a single transaction
    await this.db.transaction('rw', this.db[TABLE], async () => {
      if (toUpsert.length) {
        await this.db[TABLE].bulkPut(toUpsert);
      }
      if (toDeleteIds.length) {
        await this.db[TABLE].bulkDelete(toDeleteIds);
      }
    });
  }

  list(): Promise<PlayerForSaveDto[]> {
    return this.db[TABLE].toArray();
  }

  add(player: PlayerForSaveDto): Promise<PlayerForSaveDto> {
    return this.update(player, player.playerId);
  }

  async listPaginated(offset: number, limit: number): Promise<{ players: PlayerForSaveDto[]; total: number }> {
    const collection = this.db[TABLE].orderBy('fullName');

    const paginatedCollection = collection.offset(offset).limit(limit).toArray();
    const collectionCount = this.db[TABLE].count();

    const [players, total] = await Promise.all([paginatedCollection, collectionCount]);

    return {
      players,
      total,
    };
  }

  async queryPaginated(nameQuery: string | null, selectedAccount: string | null, myPlayerIds: number[] | null, offset: number, limit: number): Promise<{ players: PlayerForSaveDto[]; total: number }> {
    // Default initialization to full collection
    let collection: Dexie.Collection<PlayerForSaveDto, any> = this.db[TABLE].toCollection();

    // Filter by selected Accounts
    if (selectedAccount) {
      collection = this.db[TABLE].where('defaultTourName').equals(selectedAccount);
    }

    // Filter by MyPlayers
    if (Array.isArray(myPlayerIds) && myPlayerIds.length) {
      collection = collection.filter((item) => myPlayerIds.includes(item.playerId));
    }
    if (Array.isArray(myPlayerIds) && !myPlayerIds.length) {
      // If MyPlayers is checked but the user hasn't any, we show no players at all
      collection = this.db[TABLE].where('playerId').equals(-1); // impossible condition
    }

    // Filter by search Query
    if (nameQuery) {
      const all = await collection.toArray();
      const matched = searchPlayersByName(all, nameQuery);

      const total = matched.length;
      const players = matched.slice(offset, offset + limit);

      return { players, total };
    }

    const collectionCount = await collection.count();
    const paginatedCollection = await collection.offset(offset).limit(limit).toArray();

    return {
      players: paginatedCollection,
      total: collectionCount,
    };
  }

  async updatePlayerContract(playerId: number, contract: Pick<Contract, 'startDate' | 'endDate' | 'status' | 'items'>): Promise<PlayerForSaveDto> {
    return this.db.transaction('rw', this.db[TABLE], async () => {
      const player = await this.db[TABLE].get(playerId);

      if (!player) {
        throw new Error(`Player ${playerId} not found`);
      }

      // Spread current player contract and update date status and items
      const updatedPlayer: PlayerForSaveDto = {
        ...player,
        contract: {
          ...player.contract,
          startDate: contract.startDate,
          endDate: contract.endDate,
          status: contract.status,
          items: contract.items,
        },
      };

      await this.db[TABLE].put(updatedPlayer);

      return updatedPlayer;
    });
  }
}

export const PlayerDB = new PlayerDatabase(db);
